import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../constants';

interface AuthRouteProps {
  children: React.ReactNode;
  /** Czy wymagana jest autoryzacja (true = tylko dla zalogowanych, false = tylko dla niezalogowanych) */
  requireAuth?: boolean;
  /** Ścieżka przekierowania gdy warunek nie jest spełniony */
  to?: string;
}

/**
 * AuthRoute - komponent chroniący trasy przed nieautoryzowanym dostępem
 * 
 * @example
 * // Chroniona trasa (tylko dla zalogowanych)
 * <Route path="/dashboard/*" element={
 *   <AuthRoute>
 *     <Dashboard />
 *   </AuthRoute>
 * } />
 * 
 * @example
 * // Trasa tylko dla niezalogowanych (np. login, register)
 * <Route path="/login" element={
 *   <AuthRoute requireAuth={false} to="/dashboard">
 *     <Login />
 *   </AuthRoute>
 * } />
 */
const AuthRoute: React.FC<AuthRouteProps> = ({ 
  children, 
  requireAuth = true, 
  to = ROUTES.HOME
}) => {
  const { isAuthenticated, isLoading, refreshClient } = useAuth();
  const location = useLocation();

  // Odśwież dane użytkownika przy każdej zmianie ścieżki (tylko dla chronionych tras)
  useEffect(() => {
    // Wykonuj tylko dla chronionych tras, gdy użytkownik jest zalogowany i nie trwa ładowanie
    if (requireAuth && isAuthenticated && !isLoading) {
      refreshClient().catch(err => {
        console.error('Error refreshing client data:', err);
      });
    }
  }, [location.pathname, isAuthenticated, isLoading, refreshClient, requireAuth]);

  // Pokazuj loader podczas sprawdzania autoryzacji
  if (isLoading) {
    return (
      <div className="loading-state" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <div className="spinner"></div>
        <p>Ładowanie...</p>
      </div>
    );
  }

  // Dla chronionych tras (requireAuth = true)
  if (requireAuth) {
    return isAuthenticated ? <>{children}</> : <Navigate to={to} replace />;
  }
  
  // Dla tras tylko dla niezalogowanych (requireAuth = false)
  // Jeśli użytkownik jest zalogowany, przekieruj do dashboard
  return !isAuthenticated ? <>{children}</> : <Navigate to={to} replace />;
};

export default AuthRoute;

